/**
 * Identité visuelle (Q95/Q102) : le logotype affiche le branding DU TENANT
 * quand il en a un (logo + nom), sinon les défauts plateforme. Un rebranding se
 * fait donc soit dans `packages/shared/src/branding.ts` (plateforme), soit dans
 * `companies.branding` (entreprise), jamais dans ce fichier.
 */
import type { ReactNode } from 'react';
import { contrastWithWhite, PLATFORM_BRANDING, sanitizeTenantBranding, type TenantBranding } from '@myequip/shared';
import { avatarArt, initialsOf } from '../core/avatar-art.js';
import { brandLogoUrl, brandName } from '../core/branding.js';

/** Largeur / hauteur de `lockup.svg` (viewBox 5066 × 968, généré par `scripts/build-brand-vectors.mjs`). */
const LOCKUP_RATIO = 5066 / 968;

function DefaultMark({ size }: { size: number }): ReactNode {
  return (
    <img className="logo-mark" src="/favicon.svg" alt="" aria-hidden="true" width={size} height={size} />
  );
}

/**
 * Monogramme d'une entreprise sans logo : ses initiales en blanc sur sa couleur
 * principale, ou sur le visuel calculé à partir de son nom quand cette couleur
 * est trop claire pour un texte blanc. Une entreprise ne reçoit jamais le
 * symbole MyEquip à la place du sien (docs/BRAND.md).
 */
function CompanyMark({ size, name, color }: { size: number; name: string; color: string | undefined }): ReactNode {
  const background = color && contrastWithWhite(color) >= 4.5 ? color : avatarArt(name);
  return (
    <span
      className="company-mark"
      aria-hidden="true"
      style={{ '--company-mark-size': `${size}px`, '--avatar-art': background }}
    >
      {initialsOf(name)}
    </span>
  );
}

/**
 * Pictogramme seul (topbar mobile, favicon, PWA). Dans l'espace d'une
 * entreprise (`branding` ou `name` connu) : son logo, sinon son monogramme ;
 * ailleurs, le symbole MyEquip.
 */
export function LogoMark({ size = 28, branding, name }: {
  size?: number;
  branding?: TenantBranding | null | undefined;
  /** Nom de l'entreprise quand son identité n'est pas chargée (portail). */
  name?: string | undefined;
}): ReactNode {
  const logo = brandLogoUrl(branding);
  if (logo) {
    return (
      <img className="logo-mark" src={logo} alt="" aria-hidden="true" width={size} height={size} />
    );
  }
  const company = sanitizeTenantBranding(branding).displayName ?? name;
  if (company) return <CompanyMark size={size} name={company} color={branding?.primaryColor} />;
  return <DefaultMark size={size} />;
}

/** Logotype complet (side-nav, connexion, portails). */
export function Logo({ size = 28, showName = true, branding }: {
  size?: number;
  showName?: boolean;
  branding?: TenantBranding | null | undefined;
}): ReactNode {
  if (!branding) {
    if (!showName) return <LogoMark size={size} />;
    const width = Math.round(size * LOCKUP_RATIO);
    return (
      <span className="platform-wordmark" style={{ '--wordmark-width': `${width}px`, '--wordmark-height': `${size}px` }}>
        <img
          className="platform-wordmark__light"
          src="/lockup.svg"
          alt={PLATFORM_BRANDING.productName}
          width={width}
          height={size}
        />
        <img
          className="platform-wordmark__dark"
          src="/lockup-reversed.svg"
          alt={PLATFORM_BRANDING.productName}
          width={width}
          height={size}
        />
      </span>
    );
  }

  return (
    <span className="tenant-wordmark" style={{ '--wordmark-height': `${size}px` }}>
      <LogoMark size={size} branding={branding} />
      {showName ? <span className="tenant-wordmark__name">{brandName(branding)}</span> : null}
    </span>
  );
}
